import React, { useState, useEffect } from "react";
import { useMutation } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { DialogHeader, DialogTitle } from "@/components/ui/Dialog";
import toast from "react-hot-toast";
import CloseIcon from "@/components/icons/CloseIcon";

const CategoryModal = ({ isOpen, onClose, category, mode, mineTypeId, onSuccess }) => {
    const [formData, setFormData] = useState({
        risk_name: "",
        category_name: "",
    });

    // Disable background scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (mode === "edit" && category) {
            setFormData({
                risk_name: category.risk_name || "",
                category_name: category.category_name || "",
            });
        } else {
            setFormData({
                risk_name: "",
                category_name: "",
            });
        }
    }, [mode, category, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const createMutation = useMutation(
        (data) => api.post(`/mine-type/${mineTypeId}/category`, data),
        {
            onSuccess: () => {
                toast.success("Category created successfully");
                onClose();
                if (onSuccess) onSuccess();
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Failed to create category");
            },
        }
    );

    const updateMutation = useMutation(
        (data) => api.put(`/mine-type/${mineTypeId}/category/${category.id}`, data),
        {
            onSuccess: () => {
                toast.success("Category updated successfully");
                onClose();
                if (onSuccess) onSuccess();
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Failed to update category");
            },
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.risk_name.trim()) {
            toast.error("Risk Name is required");
            return;
        }

        if (!formData.category_name.trim()) {
            toast.error("Category Name is required");
            return;
        }

        const dataToSend = {
            risk_name: formData.risk_name.trim(),
            category_name: formData.category_name.trim(),
        };

        // Note: Duplicate check is handled by the API
        if (mode === "create") {
            createMutation.mutate(dataToSend);
        } else {
            updateMutation.mutate(dataToSend);
        }
    };

    const isLoading = createMutation.isLoading || updateMutation.isLoading;

    return (
        <div
            className={`fixed inset-0 z-50 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
        >
            <div
                className={`fixed inset-0 bg-white bg-opacity-50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
                onClick={onClose}
            ></div>
            <div className="fixed inset-0 flex items-center justify-center py-8 px-4 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <div
                    className={`w-full max-w-xl transform rounded-lg bg-navy-500 transition-all duration-300 ${isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"
                        } max-h-[calc(100vh-4rem)] my-auto flex flex-col sm:w-11/12 md:w-9/12 lg:w-8/12`}
                >
                    <DialogHeader className="sticky top-0 z-10 px-6 pt-6 pb-4 rounded-t-lg bg-navy-500">
                        <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-0 sm:text-xl md:text-2xl">
                            {mode === "create" ? "Add New Section" : "Edit Section"}
                            <CloseIcon
                                className="absolute w-8 h-8 text-red-500 transition-transform duration-200 cursor-pointer top-6 right-6 hover:scale-110"
                                onClick={onClose}
                            />
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                        <div className="flex-1 overflow-y-auto px-6 pt-4 mb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="risk_name">Risk Section *</Label>
                                    <Input
                                        id="risk_name"
                                        name="risk_name"
                                        value={formData.risk_name}
                                        onChange={handleChange}
                                        placeholder="Enter risk name"
                                        className="border-primary/20 focus:border-primary/40"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="category_name">Risk Sub-Section *</Label>
                                    <Input
                                        id="category_name"
                                        name="category_name"
                                        value={formData.category_name}
                                        onChange={handleChange}
                                        placeholder="Enter category name"
                                        className="border-primary/20 focus:border-primary/40"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 z-10 px-6 pt-4 pb-6 rounded-b-lg bg-navy-500">
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full gradient-primary rounded-xl sm:flex-1"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                                            {mode === "create" ? "Adding..." : "Updating..."}
                                        </>
                                    ) : mode === "create" ? (
                                        "Add"
                                    ) : (
                                        "Update"
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onClose}
                                    disabled={isLoading}
                                    className="w-full rounded-xl sm:w-auto"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CategoryModal;

